import type { ComponentPropsWithRef, ReactNode } from "react";
import { forwardRef } from "react";
import { Column, Row, Section } from "@react-email/components";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { useGoober } from "../../../Design/GooberContext";
import { mobileMediaQuery } from "../../../Design/mediaQuery";
import { useTopBlockStyles } from "../../../Design/useTopBlockStyles";
import { SectionContainer } from "../../../Utils/SectionContainer";
import { defaultStyles } from "./defaultStyles";

interface ProductShot1Props
  extends Omit<ComponentPropsWithRef<"table">, "loading"> {
  "data-preview-mode": boolean;
  footerContent?: ReactNode;
  headerContent?: ReactNode;
  productCard1?: ReactNode;
  productCard2?: ReactNode;
  productCard3?: ReactNode;
  productCard4?: ReactNode;
  productCard5?: ReactNode;
  productCard6?: ReactNode;
  styles?: Record<string, StyleValue>;
}

const columnWidth = `${100 / 3}%` as const;
const columnStyle = {
  verticalAlign: "top",
  width: columnWidth,
} as const;

const getMobileColumnStyles = ({
  skipBottomPadding = false,
}: {
  skipBottomPadding?: boolean;
} = {}) => `
  ${mobileMediaQuery} {
    display: block;
    width: 100% !important;
    padding-right: 0 !important;
    padding-bottom: ${skipBottomPadding ? "0" : "48px"};
  }
`;

export const ProductShot1 = forwardRef<HTMLTableElement, ProductShot1Props>(
  (
    {
      headerContent,
      productCard1,
      productCard2,
      productCard3,
      productCard4,
      productCard5,
      productCard6,
      footerContent,
      styles = {},
      ...rest
    },
    ref,
  ) => {
    const { css } = useGoober();

    const { backgroundStyles, paddingStyles, marginStyles } = useTopBlockStyles(
      {
        styles,
        defaultStyles,
      },
    );

    return (
      <SectionContainer cssProperties={marginStyles}>
        <Section
          ref={ref}
          style={{
            ...paddingStyles,
            ...backgroundStyles,
          }}
          {...rest}
        >
          <Row>
            <Column>{headerContent}</Column>
          </Row>
          <Row>
            <Column
              style={columnStyle}
              className={css`
                ${getMobileColumnStyles()}
              `}
            >
              {productCard1}
            </Column>
            <Column
              style={columnStyle}
              className={css`
                ${getMobileColumnStyles()}
              `}
            >
              {productCard2}
            </Column>
            <Column
              style={columnStyle}
              className={css`
                ${getMobileColumnStyles({ skipBottomPadding: true })}
              `}
            >
              {productCard3}
            </Column>
          </Row>
          <Row>
            <Column
              style={columnStyle}
              className={css`
                ${getMobileColumnStyles()}
              `}
            >
              {productCard4}
            </Column>
            <Column
              style={columnStyle}
              className={css`
                ${getMobileColumnStyles()}
              `}
            >
              {productCard5}
            </Column>
            <Column
              style={columnStyle}
              className={css`
                ${getMobileColumnStyles({ skipBottomPadding: true })}
              `}
            >
              {productCard6}
            </Column>
          </Row>
          <Row>
            <Column>{footerContent}</Column>
          </Row>
        </Section>
      </SectionContainer>
    );
  },
);

ProductShot1.displayName = "ProductShot1";
